import { Calendar, Clock, Globe } from "lucide-react";

import { Lockup, Mark } from "../components/Mark";
import { Page, SectionBlock } from "../components/Page";

function HeroMock() {
  return (
    <div
      className="surface relative overflow-hidden p-8"
      style={{
        background:
          "linear-gradient(180deg, var(--bg) 0%, var(--bg-soft) 60%, var(--paper) 100%)",
        minHeight: 360,
      }}
    >
      <div className="flex items-center justify-between">
        <Lockup
          glyphSize={32}
          wordmarkSize={11}
          taglineSize={10}
          showTagline={false}
        />
        <span className="font-body text-[12px] text-ink-soft">
          Come sit with us · The 11-Day Challenge →
        </span>
      </div>

      <div className="mt-16 max-w-[520px]">
        <p
          className="font-flair text-[22px] text-amber"
          style={{ transform: "rotate(-2deg)", display: "inline-block" }}
        >
          something quieter
        </p>
        <h3
          className="display mt-3 text-ink"
          style={{ fontSize: "clamp(2rem, 3.2vw, 2.6rem)" }}
        >
          Ten minutes
          <br />
          in the morning.
          <br />
          <em className="italic text-amber">Ten in the evening.</em>
        </h3>
      </div>
    </div>
  );
}

function AdMock() {
  return (
    <div
      className="surface relative overflow-hidden p-7"
      style={{ background: "var(--paper)", minHeight: 320 }}
    >
      <Lockup glyphSize={44} wordmarkSize={12} taglineSize={11} />

      <div className="mt-6 max-w-[420px]">
        <h3
          className="display text-ink"
          style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)" }}
        >
          Pause the noise.
          <br />
          <em className="italic text-amber">Reconnect with yourself.</em>
        </h3>
        <p className="mt-3 font-body text-[12px] leading-relaxed text-ink-soft">
          Helps reduce stress, calm the mind, and experience deep inner
          stillness through a simple guided meditation practice.
        </p>
        <span
          className="mt-3 inline-flex rounded-md px-3 py-1.5 font-body text-[12px] font-medium"
          style={{
            background: "var(--peach-deep)",
            color: "var(--ink)",
          }}
        >
          Just ten minutes a day
        </span>
      </div>

      <div className="absolute right-7 bottom-7 flex flex-col gap-2">
        <div
          className="surface flex items-center gap-2 px-3 py-2"
          style={{ background: "var(--bg-soft)" }}
        >
          <Calendar size={16} color="var(--amber)" />
          <span className="font-display text-[14px] text-ink">
            Every Sunday
          </span>
        </div>
        <div
          className="surface flex items-center gap-2 px-3 py-2"
          style={{ background: "var(--bg-soft)" }}
        >
          <Clock size={16} color="var(--amber)" />
          <span className="font-display text-[14px] text-ink">10:00 am</span>
        </div>
      </div>
    </div>
  );
}

function SocialMock() {
  return (
    <div
      className="surface aspect-square overflow-hidden p-6"
      style={{
        background:
          "linear-gradient(180deg, var(--bg) 0%, var(--bg-soft) 100%)",
      }}
    >
      <Lockup
        glyphSize={30}
        wordmarkSize={10}
        taglineSize={9}
        showTagline={false}
      />
      <div className="mt-12 flex h-full flex-col justify-center">
        <p className="font-flair text-[20px] text-amber">just for today</p>
        <h3
          className="display mt-2 text-ink"
          style={{ fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}
        >
          Twenty minutes is <em className="italic text-amber">enough.</em>
        </h3>
      </div>
    </div>
  );
}

function EmailMock() {
  return (
    <div
      className="surface overflow-hidden"
      style={{ background: "var(--paper)" }}
    >
      <div className="bg-ink p-4">
        <Lockup
          glyphSize={28}
          wordmarkSize={10}
          taglineSize={9}
          showTagline={false}
          tone="light"
        />
      </div>
      <div className="p-5">
        <p className="font-display text-[20px] text-ink">
          Thanks for joining, Adi.
        </p>
        <p className="mt-3 font-body text-[13px] leading-relaxed text-ink-soft">
          We'll see you at Sunday's session. If you'd like to begin with the
          eleven-day challenge in the meantime, the door is at{" "}
          <span className="text-amber">vyaustralia.org/challenge</span>.
        </p>
        <p className="mt-4 font-body text-[12px] text-ink-soft">
          Warmly,
          <br />
          <span className="font-display italic text-amber">
            Vihangam Yog Australia
          </span>
        </p>
      </div>
    </div>
  );
}

function BusinessCard() {
  return (
    <div
      className="surface flex items-center justify-between p-6"
      style={{ background: "var(--paper)", aspectRatio: "1.75 / 1" }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Mark size={40} />
          <div className="flex flex-col gap-0.5 leading-tight">
            <span className="font-body text-[10px] font-semibold tracking-[0.28em] uppercase text-ink">
              Vihangam Yog
            </span>
            <span className="font-display text-[10px] italic text-ink-soft">
              Sydney
            </span>
          </div>
        </div>
        <p className="font-display text-[14px] italic text-ink">
          Come sit with us.
        </p>
      </div>
      <div className="flex flex-col items-end gap-0.5 text-right">
        <p className="font-body text-[11px] text-ink-soft">vyaustralia.org</p>
        <p className="font-body text-[11px] text-ink-soft">
          info@vyaustralia.org
        </p>
        <div className="mt-2 flex items-center gap-1.5">
          <Globe size={12} color="var(--amber)" />
          <span className="font-body text-[10px] tracking-[0.18em] uppercase text-amber">
            Sydney · online
          </span>
        </div>
      </div>
    </div>
  );
}

export function Applications() {
  return (
    <Page
      number="08"
      title="Applications"
      eyebrow="The brand in use"
      intro={
        <>
          A reference set of how the system lands in the surfaces we actually
          make. Each mock below reads the active variant, so toggle to feel how
          the brand carries across.
        </>
      }
    >
      <SectionBlock title="Website hero">
        <HeroMock />
      </SectionBlock>

      <SectionBlock title="Campaign ad (square)">
        <AdMock />
      </SectionBlock>

      <SectionBlock title="Social post">
        <div className="grid gap-4 md:grid-cols-2">
          <SocialMock />
          <div
            className="surface flex flex-col justify-center p-7"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">Notes</p>
            <span className="hairline" />
            <ul className="mt-5 space-y-3 font-body text-[14px] text-ink-soft">
              {[
                "Square format: 1080 × 1080.",
                "One headline, one moment of flair, no more.",
                "अ glyph as the only logo treatment.",
                "Never overlay headlines on photography — use paper underlay.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Social formats — refined library">
        <p className="max-w-[680px] font-body text-[15px] leading-relaxed text-ink-soft">
          A second pass that drops hand-drawn ornaments in favour of real
          photography (generated via the OpenAI image API per the brand's
          imagery direction) and uses Lucide SVG icons. The typographic
          hierarchy is more deliberate — usually one weighted moment per
          surface, the rest in measured weight. Each ad below shows a different
          compositional principle.
        </p>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {[
            {
              src: "/applications/v2/ad_editorial_horizon.png",
              tag: "Photo-led editorial",
              note: "Full-bleed cinematic photograph; one short italic headline overlay; tiny brand mark; restrained footer. Photo does the emotional work — type stays quiet.",
            },
            {
              src: "/applications/v2/ad_split_hands.png",
              tag: "Asymmetric split",
              note: 'Photo column + type column. A quiet question in regular Fraunces, then ONE emphatic line ("Just sit."). Only the answer carries weight.',
            },
            {
              src: "/applications/v2/ad_number_candle.png",
              tag: "Single number, atmospheric",
              note: 'Centered photograph; large "10" in *light* Fraunces (400), not bold — the candle\'s warmth carries presence. Supporting copy in soft weight beneath.',
            },
            {
              src: "/applications/v2/ad_invite_mountains.png",
              tag: "Banner photo + info band",
              note: "Photo top 60%, cream type band bottom 40%. Lucide SVG icons (calendar / clock / map-pin), single italic accent line. No bold on every line.",
            },
          ].map((p) => (
            <figure
              key={p.src}
              className="surface overflow-hidden"
              style={{ background: "var(--paper)" }}
            >
              <div className="aspect-square w-full bg-bg-soft">
                <img
                  src={p.src}
                  alt={p.tag}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <figcaption className="p-4">
                <p className="eyebrow text-amber">{p.tag}</p>
                <p className="mt-2 font-body text-[13px] leading-relaxed text-ink-soft">
                  {p.note}
                </p>
              </figcaption>
            </figure>
          ))}
        </div>

        <div
          className="surface mt-6 grid gap-6 p-7 md:grid-cols-2"
          style={{ background: "var(--paper)" }}
        >
          <div>
            <p className="eyebrow">Hierarchy principles</p>
            <span className="hairline" />
            <ul className="mt-5 space-y-3 font-body text-[14px] text-ink-soft">
              {[
                "One weighted moment per surface. Usually the headline or a single italic phrase.",
                "Brand mark in measured weight — never bold. Wide tracking carries the formality.",
                "Body copy in regular Fraunces or Inter. No subheads in bold.",
                "Numerals lean light (Fraunces 400) when the photograph carries presence; heavier (600) when the number IS the message.",
                "Footer rows: small Inter, subtle tracking, never shouting.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
                  {l}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow">Assets used</p>
            <span className="hairline" />
            <ul className="mt-5 space-y-3 font-body text-[14px] text-ink-soft">
              {[
                "Photography: OpenAI gpt-image-1 with brand-shaped prompts (see prompts.ts).",
                "Icons: Lucide line icons (calendar, clock, map-pin) — inline SVG. No hand-drawn paths.",
                "Decorative motifs: kept minimal — hairlines, soft vignettes. No corner blobs.",
                "Type: Fraunces (display, variable opsz + SOFT) + Inter (body).",
                "Flair (5%): Caveat for the one Caveat-rendered phrase per surface.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Social formats — earlier library">
        <p className="max-w-[640px] font-body text-[15px] leading-relaxed text-ink-soft">
          A working set of square and story-format posts the brand has shipped.
          Each demonstrates a different content angle — photo-led atmosphere,
          the fact card, the Sanskrit word study, the scheduling card, the
          vertical story. All rendered from the campaign templates under{" "}
          <code className="font-display text-[13px] text-amber">
            projects/vihangam-yog/campaign/templates/
          </code>
          .
        </p>

        {/* Square ads row */}
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {[
            {
              src: "/applications/ad-photo-quote.png",
              tag: "Photo-led atmosphere",
              note: "Full-bleed photography with ink wash. A Caveat eyebrow and one short quote in Fraunces italic. For days the feed wants quiet, not information.",
            },
            {
              src: "/applications/ad-twenty-minutes.png",
              tag: "Single-fact card",
              note: "A bold number (Fraunces 700, SOFT 30) with a small italic unit and an explainer line. Used to land one specific commitment loudly: 20 minutes, 11 days, 1 hour, free.",
            },
            {
              src: "/applications/ad-sanskrit-vihangam.png",
              tag: "Word study (Sanskrit)",
              note: "Devanagari at full presence in saffron, romanised below, a one-paragraph definition. Knowledge-led; reinforces lineage without performing it.",
            },
            {
              src: "/applications/ad-sunday-schedule.png",
              tag: "Schedule / information",
              note: "Three icon cards (when · time · where), filled CTA pill, free stamp. Use when the post needs to do the registration work itself.",
            },
          ].map((p) => (
            <figure
              key={p.src}
              className="surface overflow-hidden"
              style={{ background: "var(--paper)" }}
            >
              <div className="aspect-square w-full bg-bg-soft">
                <img
                  src={p.src}
                  alt={p.tag}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <figcaption className="p-4">
                <p className="eyebrow text-amber">{p.tag}</p>
                <p className="mt-2 font-body text-[13px] leading-relaxed text-ink-soft">
                  {p.note}
                </p>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Vertical story */}
        <div className="mt-6 grid gap-5 md:grid-cols-[280px_1fr]">
          <figure
            className="surface overflow-hidden"
            style={{ background: "var(--paper)" }}
          >
            <div className="aspect-[9/16] w-full bg-bg-soft">
              <img
                src="/applications/ad-story-vertical.png"
                alt="Vertical story format"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <figcaption className="p-4">
              <p className="eyebrow text-amber">Story · 1080 × 1920</p>
              <p className="mt-2 font-body text-[12px] leading-relaxed text-ink-soft">
                Vertical format for Instagram + Facebook stories.
              </p>
            </figcaption>
          </figure>

          <div
            className="surface flex flex-col justify-center p-7"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">Format notes</p>
            <span className="hairline" />
            <ul className="mt-5 space-y-3 font-body text-[14.5px] text-ink-soft">
              {[
                "Square posts: 1080 × 1080 (Instagram, Facebook feed).",
                "Stories: 1080 × 1920 (Instagram, Facebook stories).",
                "Photo + ink wash for stories; cream-led backgrounds for fact / word-study / schedule cards.",
                "One headline, one moment of flair, no more — across every format.",
                "अ glyph as the canonical mark; never substitute a stylised letter.",
                "Footer strip on every ad carries the URL or session details — never both.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Transactional email">
        <div className="grid gap-4 md:grid-cols-2">
          <EmailMock />
          <div
            className="surface flex flex-col justify-center p-7"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">Notes</p>
            <span className="hairline" />
            <ul className="mt-5 space-y-3 font-body text-[14px] text-ink-soft">
              {[
                "First-person warmth. Sign off as the organisation, not a person.",
                "Two paragraphs maximum for transactional flows.",
                "No big CTA buttons — text links carry the warmth better.",
                "Don't include marketing in transactional. They get separate emails.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-3 shrink-0 bg-amber" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Business card">
        <BusinessCard />
        <p className="mt-5 max-w-[640px] font-body text-[14px] leading-relaxed text-ink-soft">
          85 × 49mm. Sand front, ink back. No phone numbers — we'd rather start
          the relationship over email or in person.
        </p>
      </SectionBlock>
    </Page>
  );
}
